/**
 * Turf War — pure local 1v1 territory engine.
 *
 * Five FIVE-foot scoring zones — 10–15, 20–25, 30–35, 40–45, 50–55 — separated
 * by five-foot DEAD ZONES that score nothing. Players alternate ten regulation
 * putts each. More balls in a zone controls it and every equal count is
 * neutral. Most controlled zones after regulation wins.
 *
 * ⚠️ The gaps are the game (Cole, 2026-08-22: *"it's too boring and easy to
 * have 10-60' all be viable zones"*). The zones used to tile the whole 10–60 ft
 * range, so every legal putt scored somewhere and the only skill was choosing
 * which band to aim at. With half the range dead, a putt has to actually land
 * where it was aimed — and a miss by five feet costs the turn rather than
 * quietly banking a ball in the neighbouring zone.
 *
 * Bands are CLOSED at both ends. That is unambiguous only because of the gaps:
 * zone 0 ends at 15 and zone 1 starts at 20, so no distance belongs to two
 * zones. The old tiling needed half-open bands plus a special case for the top
 * edge; with dead zones both edges of every band can behave the same way, and
 * a putt that stops exactly on a line is generously counted in.
 *
 * Because five territories can finish 2–2 with a neutral zone, tied matches
 * enter a paired sudden-death shootout on the center-most neutral territory.
 * Both players receive one attempt. Exactly one hit wins; if both hit or both
 * miss, the next pair starts with the other player.
 */

export type TurfWarPlayer = 0 | 1;
export type TurfWarPhase = 'regulation' | 'suddenDeath' | 'complete';

/**
 * 10 → 5 (Cole, 2026-08-23: "change from 20 total putts to 10 total putts").
 * The count is per PLAYER; the match total is twice this, which is the number
 * the bottom-right counter shows and the number Cole is naming.
 */
export const TURF_WAR_PUTTS_PER_PLAYER = 5;
/** Bottom of the first scoring band. */
export const TURF_WAR_MIN_FT = 10;
/** Top of the LAST scoring band — not the mode's putting ceiling. Anything
 *  past this is a dead miss, same as landing in a gap. */
export const TURF_WAR_MAX_FT = 55;
/** How wide a scoring band is, and equally how wide the dead gap after it is. */
export const TURF_WAR_ZONE_WIDTH_FT = 5;
/** Band n starts this far past band n-1: five live feet plus five dead ones. */
export const TURF_WAR_ZONE_STRIDE_FT = 10;

export interface TurfWarZone {
  index: number;
  minFt: number;
  maxFt: number;
  midpointFt: number;
  label: string;
}

export const TURF_WAR_ZONES: readonly TurfWarZone[] = Array.from(
  { length: 5 },
  (_, index) => {
    const minFt = TURF_WAR_MIN_FT + index * TURF_WAR_ZONE_STRIDE_FT;
    const maxFt = minFt + TURF_WAR_ZONE_WIDTH_FT;
    return {
      index,
      minFt,
      maxFt,
      midpointFt: (minFt + maxFt) / 2,
      label: `${minFt}–${maxFt}`,
    };
  }
);

/**
 * The gaps, for the surfaces that have to DRAW them — the green's rollout
 * bands and the HUD (Cole: *"maybe shade them like black or something"*). A
 * dead zone the player cannot see is just an unexplained miss.
 *
 * Derived from the scoring zones rather than listed, so the two can never
 * drift apart if the bands are ever retuned.
 */
export const TURF_WAR_DEAD_ZONES: readonly { minFt: number; maxFt: number }[] =
  TURF_WAR_ZONES.slice(0, -1).map((zone, index) => ({
    minFt: zone.maxFt,
    maxFt: TURF_WAR_ZONES[index + 1].minFt,
  }));

export interface TurfWarShot {
  playerIndex: TurfWarPlayer;
  distanceFt: number;
  zoneIndex: number | null;
  phase: 'regulation' | 'suddenDeath';
  suddenDeathRound: number | null;
}

export interface TurfWarZoneStanding {
  zoneIndex: number;
  counts: [number, number];
  controllerIndex: TurfWarPlayer | null;
}

export interface TurfWarSuddenDeath {
  zoneIndex: number;
  round: number;
  openerIndex: TurfWarPlayer;
  attempts: [TurfWarShot | null, TurfWarShot | null];
}

export interface TurfWarState {
  firstPlayerIndex: TurfWarPlayer;
  turnIndex: TurfWarPlayer;
  phase: TurfWarPhase;
  regulationPutts: [number, number];
  shots: TurfWarShot[];
  winnerIndex: TurfWarPlayer | null;
  suddenDeath: TurfWarSuddenDeath | null;
}

export type TurfWarPuttVerdict =
  | {
      kind: 'landed';
      playerIndex: TurfWarPlayer;
      zoneIndex: number;
      controllerIndex: TurfWarPlayer | null;
      captured: boolean;
    }
  | { kind: 'miss'; playerIndex: TurfWarPlayer }
  | { kind: 'ignored' };

export interface AppliedTurfWarPutt {
  state: TurfWarState;
  verdict: TurfWarPuttVerdict;
}

/**
 * The distance the NEXT putt is aimed at, for the player's history, or null
 * when the rules name none. Regulation lets a player pick any zone and the app
 * never learns which, so it has no aimed distance. Sudden death names one
 * zone both players must hit, so its midpoint is the aim.
 */
export function turfWarAimedDistanceFt(state: TurfWarState): number | null {
  if (state.phase !== 'suddenDeath' || !state.suddenDeath) return null;
  return TURF_WAR_ZONES[state.suddenDeath.zoneIndex]?.midpointFt ?? null;
}

export function createTurfWarGame(firstPlayerIndex: TurfWarPlayer = 0): TurfWarState {
  return {
    firstPlayerIndex,
    turnIndex: firstPlayerIndex,
    phase: 'regulation',
    regulationPutts: [0, 0],
    shots: [],
    winnerIndex: null,
    suddenDeath: null,
  };
}

/**
 * Which scoring band a roll-out landed in, or null for a dead zone.
 *
 * Closed bands on both ends — see the header. Returns null for anything short
 * of the first band, past the last, or in one of the gaps between them; the
 * caller treats all three the same way, as a spent turn.
 */
export function turfWarZoneForDistance(distanceFt: number): number | null {
  if (!Number.isFinite(distanceFt)) return null;
  if (distanceFt < TURF_WAR_MIN_FT || distanceFt > TURF_WAR_MAX_FT) return null;
  const offset = distanceFt - TURF_WAR_MIN_FT;
  const zoneIndex = Math.floor(offset / TURF_WAR_ZONE_STRIDE_FT);
  if (zoneIndex < 0 || zoneIndex >= TURF_WAR_ZONES.length) return null;
  // Inside the stride, only the first five feet score. The remainder is the
  // dead gap — except exactly on a band's upper edge, which counts.
  const withinStride = offset - zoneIndex * TURF_WAR_ZONE_STRIDE_FT;
  return withinStride <= TURF_WAR_ZONE_WIDTH_FT ? zoneIndex : null;
}

function shotFor(
  playerIndex: TurfWarPlayer,
  distanceFt: number,
  phase: 'regulation' | 'suddenDeath',
  suddenDeathRound: number | null
): TurfWarShot {
  const zoneIndex = turfWarZoneForDistance(distanceFt);
  return {
    playerIndex,
    distanceFt,
    zoneIndex,
    phase,
    suddenDeathRound,
  };
}

export function turfWarZoneStanding(
  shots: readonly TurfWarShot[],
  zoneIndex: number
): TurfWarZoneStanding {
  const counts: [number, number] = [0, 0];

  for (const shot of shots) {
    if (shot.zoneIndex !== zoneIndex) continue;
    counts[shot.playerIndex] += 1;
  }

  let controllerIndex: TurfWarPlayer | null = null;
  if (counts[0] > counts[1]) controllerIndex = 0;
  else if (counts[1] > counts[0]) controllerIndex = 1;

  return {
    zoneIndex,
    counts,
    controllerIndex,
  };
}

export function turfWarStandings(state: TurfWarState): TurfWarZoneStanding[] {
  // Regulation owns the initial territory ledger. During sudden death, only
  // hits in the assigned target band count. Completed hit-hit pairs add one
  // ball per side and therefore remain visibly neutral. Once exactly one
  // player lands in the band, that winning hit creates a real one-ball lead;
  // the finished board never colors a row whose displayed counts are tied.
  const scoringShots = state.shots.filter((shot) => {
    if (shot.phase === 'regulation') return true;
    if (!state.suddenDeath || shot.zoneIndex !== state.suddenDeath.zoneIndex) {
      return false;
    }
    if (state.phase === 'complete') return true;
    return (
      shot.suddenDeathRound !== null &&
      shot.suddenDeathRound < state.suddenDeath.round
    );
  });
  const standings = TURF_WAR_ZONES.map((zone) =>
    turfWarZoneStanding(scoringShots, zone.index)
  );
  return standings;
}

export function turfWarScore(state: TurfWarState): [number, number] {
  const score: [number, number] = [0, 0];
  for (const standing of turfWarStandings(state)) {
    if (standing.controllerIndex !== null) score[standing.controllerIndex] += 1;
  }
  return score;
}

/**
 * Who has already won, if the match is mathematically decided (Cole,
 * 2026-08-23: "if you have the match clinched it should end early"). Null while
 * the trailing player can still tie or win.
 *
 * 🔴 Deliberately a BOUND, not a guess. It gives the trailing player their
 * best case — every remaining putt lands exactly where it does the most good,
 * cheapest zone first — and only calls it over if they still fall short. A
 * clinch that ended a winnable match would take the game away from someone who
 * was about to come back, which is far worse than playing two dead putts.
 *
 * A TIE is not clinched: level scores go to sudden death, so the trailer only
 * needs to reach parity to keep playing.
 */
export function turfWarClinchedWinner(state: TurfWarState): TurfWarPlayer | null {
  if (state.phase !== 'regulation') return null;
  const score = turfWarScore(state);
  if (score[0] === score[1]) return null;
  const leader: TurfWarPlayer = score[0] > score[1] ? 0 : 1;
  const trailer: TurfWarPlayer = (1 - leader) as TurfWarPlayer;
  const remaining = TURF_WAR_PUTTS_PER_PLAYER - state.regulationPutts[trailer];
  if (remaining <= 0) return null;

  // Per zone, what the trailer's remaining putts could buy. Two distinct moves,
  // and MISSING THE CHEAPER ONE is how a first attempt at this wrongly ended a
  // match that was still alive: NEUTRALISING a zone the leader holds costs one
  // putt less than taking it and already closes the gap by one, which can be
  // enough to force sudden death.
  const options = turfWarStandings(state).map((standing) => {
    if (standing.controllerIndex === trailer) return [{ cost: 0, gain: 0 }];
    const deficit = standing.counts[leader] - standing.counts[trailer];
    const held = standing.controllerIndex === leader;
    return [
      { cost: 0, gain: 0 },
      // Level it: the leader loses the zone, the trailer does not gain it.
      ...(held ? [{ cost: deficit, gain: 1 }] : []),
      // Take it outright: worth two to the margin when prised off the leader.
      { cost: deficit + 1, gain: held ? 2 : 1 },
    ];
  });

  // Five zones, at most three choices each — 243 combinations, so this is
  // solved EXACTLY rather than greedily. A greedy pick is not safe here: a
  // cheap neutralise can beat an expensive take that looks better per zone.
  let bestGain = 0;
  const walk = (index: number, budget: number, gain: number) => {
    if (index === options.length) {
      if (gain > bestGain) bestGain = gain;
      return;
    }
    for (const option of options[index]) {
      if (option.cost <= budget) walk(index + 1, budget - option.cost, gain + option.gain);
    }
  };
  walk(0, remaining, 0);

  // A tie sends the match to sudden death, so the trailer only has to REACH
  // level. The leader's own remaining putts can only help them, so leaving
  // those out is the conservative direction.
  const margin = score[leader] - score[trailer];
  return bestGain < margin ? leader : null;
}

/** Center-most currently neutral territory, with zone C preferred. */
export function suddenDeathZoneFor(state: TurfWarState): number {
  const neutral = turfWarStandings(state)
    .filter((standing) => standing.controllerIndex === null)
    .map((standing) => standing.zoneIndex);
  if (neutral.length === 0) return 2;
  return neutral.sort((a, b) => Math.abs(a - 2) - Math.abs(b - 2) || a - b)[0];
}

function playoffHit(shot: TurfWarShot | null, zoneIndex: number): boolean {
  return shot?.zoneIndex === zoneIndex;
}

export function applyTurfWarPutt(
  state: TurfWarState,
  distanceFt: number
): AppliedTurfWarPutt {
  if (state.phase === 'complete') {
    return { state, verdict: { kind: 'ignored' } };
  }

  const playerIndex = state.turnIndex;
  const nextPlayerIndex = (1 - playerIndex) as TurfWarPlayer;
  const inPlayoff = state.phase === 'suddenDeath';
  const shot = shotFor(
    playerIndex,
    distanceFt,
    inPlayoff ? 'suddenDeath' : 'regulation',
    state.suddenDeath?.round ?? null
  );
  const shots = [...state.shots, shot];
  const regulationPutts: [number, number] = [...state.regulationPutts];
  if (!inPlayoff) regulationPutts[playerIndex] += 1;

  let next: TurfWarState = {
    ...state,
    turnIndex: nextPlayerIndex,
    regulationPutts,
    shots,
  };

  // Decided before the ledger runs out — stop rather than play dead putts
  // (Cole, 2026-08-23). Checked BEFORE the full-ledger branch so a match that
  // clinches on the final pair still resolves through the same winner path.
  const clinched = !inPlayoff ? turfWarClinchedWinner(next) : null;
  if (clinched !== null) {
    next = { ...next, phase: 'complete', winnerIndex: clinched };
  } else if (!inPlayoff && regulationPutts[0] === TURF_WAR_PUTTS_PER_PLAYER &&
      regulationPutts[1] === TURF_WAR_PUTTS_PER_PLAYER) {
    const score = turfWarScore(next);
    if (score[0] !== score[1]) {
      next = {
        ...next,
        phase: 'complete',
        winnerIndex: score[0] > score[1] ? 0 : 1,
      };
    } else {
      const zoneIndex = suddenDeathZoneFor(next);
      next = {
        ...next,
        phase: 'suddenDeath',
        turnIndex: state.firstPlayerIndex,
        suddenDeath: {
          zoneIndex,
          round: 1,
          openerIndex: state.firstPlayerIndex,
          attempts: [null, null],
        },
      };
    }
  } else if (inPlayoff && state.suddenDeath) {
    const attempts: [TurfWarShot | null, TurfWarShot | null] = [
      ...state.suddenDeath.attempts,
    ];
    attempts[playerIndex] = shot;
    const otherIndex = (1 - state.suddenDeath.openerIndex) as TurfWarPlayer;
    const pairComplete = attempts[state.suddenDeath.openerIndex] !== null &&
      attempts[otherIndex] !== null;

    if (!pairComplete) {
      next = {
        ...next,
        suddenDeath: { ...state.suddenDeath, attempts },
      };
    } else {
      const redHit = playoffHit(attempts[0], state.suddenDeath.zoneIndex);
      const blueHit = playoffHit(attempts[1], state.suddenDeath.zoneIndex);
      if (redHit !== blueHit) {
        next = {
          ...next,
          phase: 'complete',
          winnerIndex: redHit ? 0 : 1,
          suddenDeath: { ...state.suddenDeath, attempts },
        };
      } else {
        const openerIndex = (1 - state.suddenDeath.openerIndex) as TurfWarPlayer;
        next = {
          ...next,
          turnIndex: openerIndex,
          suddenDeath: {
            ...state.suddenDeath,
            round: state.suddenDeath.round + 1,
            openerIndex,
            attempts: [null, null],
          },
        };
      }
    }
  }

  const playoffTarget = inPlayoff ? state.suddenDeath?.zoneIndex ?? null : null;
  const landedInScoringZone =
    shot.zoneIndex !== null && (!inPlayoff || shot.zoneIndex === playoffTarget);
  const zoneStanding =
    !landedInScoringZone || shot.zoneIndex === null
      ? null
      : turfWarStandings(next)[shot.zoneIndex];
  const verdict: TurfWarPuttVerdict =
    !landedInScoringZone || shot.zoneIndex === null
      ? { kind: 'miss', playerIndex }
      : {
          kind: 'landed',
          playerIndex,
          zoneIndex: shot.zoneIndex,
          controllerIndex: zoneStanding?.controllerIndex ?? null,
          captured: zoneStanding?.controllerIndex === playerIndex,
        };

  return { state: next, verdict };
}
